import React, { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@/contexts";
import { Avatar } from "@/components";
import { slugify } from "@/utils";
import "./Navbar.css";

interface NavbarProps {
  open: boolean;
  onClose: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ open, onClose }) => {
  const { user, userData, logout } = useUser();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  const _isLoggedIn = Boolean(user);

  const profileImageUrl = useMemo(() => {
    const img = userData?.profileImage;
    if (!img) return null;

    // Se for string pura (formato legado), mostra direto
    if (typeof img === "string") {
      return img;
    }

    // Se for o objeto novo, só mostra se estiver aprovada
    if (typeof img === "object" && img.approved !== false) {
      return img.url;
    }

    return null;
  }, [userData?.profileImage]);

  const initials = useMemo(() => {
    const base = userData?.username;
    return base
      ?.trim()
      .split(/\s+/)
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }, [userData?.username]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  const go = (path: string) => () => {
    onClose();
    navigate(path);
  };

  const handleLogout = async () => {
    const confirmClose = window.confirm("Deseja mesmo sair da sua conta?");

    if (!confirmClose) return;

    onClose();

    try {
      await logout();
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Erro ao deslogar:", error);
    }
  };

  return (
    <>
      <div
        className={`navbar-overlay ${open ? "open" : ""}`}
        onClick={onClose}
        aria-hidden={!open}
      />
      <div
        className={`navbar-menu ${open ? "open" : ""}`}
        ref={panelRef}
        role="menu"
      >
        <div className="navbar-header">
          <div className={` ${_isLoggedIn ? "" : "guest"}`}>
            {profileImageUrl ? (
              <Avatar src={profileImageUrl} alt={userData?.username} />
            ) : (
              <span className="navbar-avatar-fallback">{initials}</span>
            )}
          </div>
          <div className="navbar-user">
            <strong className="navbar-name">
              {_isLoggedIn ? userData?.username : "Olá, convidado(a)"}
            </strong>
            {_isLoggedIn && userData && userData.username ? (
              <button
                type="button"
                className="navbar-link"
                onClick={() => {
                  navigate("/perfil/" + slugify(userData.username!));
                  onClose();
                }}
              >
                Ver meu Perfil
              </button>
            ) : (
              <span className="navbar-subtitle">Acesse sua conta</span>
            )}
          </div>
          <button
            type="button"
            className="navbar-close"
            aria-label="Fechar menu"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {_isLoggedIn ? (
          <>
            {/* <button
              type="button"
              className="navbar-primary"
              onClick={go("/planos")}
            >
              Assinar
            </button> */}

            {/* <div className="navbar-invite">
              <div className="invite-icon" aria-hidden="true">
                +
              </div>
              <div>
                <div className="invite-title">Indique seus amigos</div>
                <div className="invite-subtitle">Ganhe 30 dias Premium.</div>
              </div>
            </div> */}

            <div className="navbar-list" aria-label="Navegação do usuário">
              <button type="button" onClick={go("/inicio")}>
                Início
              </button>
              <button type="button" onClick={go("/configuracoes")}>
                Configurações e atividade
              </button>
              <button type="button" onClick={go("/pagamentos")}>
                Meus Pagamentos
              </button>
              <button type="button" onClick={go("/indicacoes")}>
                Minhas Indicações
              </button>
              <button type="button" onClick={go("/como-funciona")}>
                Como Funciona
              </button>
              <button
                type="button"
                className="navbar-link-danger"
                onClick={handleLogout}
              >
                Sair
              </button>
            </div>
          </>
        ) : (
          <div className="navbar-list" aria-label="Navegação do convidado">
            <button type="button" onClick={go("/login")}>
              Entrar
            </button>
            <button type="button" onClick={go("/como-funciona")}>
              Como Funciona
            </button>
            <button type="button" onClick={go("/sobre-nos")}>
              Sobre Nós
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Navbar;
